import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles, Chip } from '@material-ui/core'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

const useStyles = makeStyles(() => ({
  root: {
    padding: '24px 28px',
    borderRadius: 8,
    background: 'var(--opal-surface-default, #fff)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06)',
    borderLeft: '4px solid var(--color-neutral-300, #d1d5db)',
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },
  rootInformational: { borderLeftColor: 'var(--color-info-500, #0284c7)' },
  rootNotable: { borderLeftColor: 'var(--color-warning-600, #d97706)' },
  rootConcerning: { borderLeftColor: 'var(--color-danger-600, #dc2626)' },

  badges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8
  },
  badge: {
    height: 26,
    fontSize: 12,
    fontWeight: 'var(--font-weight-semibold, 600)',
    paddingInline: 4
  },
  summary: {
    fontSize: 15,
    lineHeight: 1.6,
    color: 'var(--color-neutral-900, #111827)',
    wordBreak: 'break-word',
    '& > *:first-child': { marginTop: 0 },
    '& > *:last-child': { marginBottom: 0 },
    '& p': { margin: '0 0 12px' },
    '& ul, & ol': { margin: '0 0 12px', paddingLeft: 22 },
    '& li': { margin: '2px 0' },
    '& li > p': { margin: 0 },
    '& strong': {
      color: 'var(--color-neutral-900, #111827)',
      fontWeight: 'var(--font-weight-semibold, 600)'
    },
    '& em': { fontStyle: 'italic' },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      fontSize: 14,
      fontWeight: 'var(--font-weight-bold, 700)',
      margin: '12px 0 6px',
      lineHeight: 1.3
    },
    '& a': {
      color: 'var(--color-ai-700, #6b21a8)',
      textDecoration: 'underline'
    },
    '& code': {
      fontFamily: 'var(--font-mono, "SF Mono", Menlo, Consolas, monospace)',
      fontSize: '0.9em',
      backgroundColor: 'var(--color-neutral-100, #f3f4f6)',
      padding: '1px 5px',
      borderRadius: 3
    },
    '& pre': {
      fontFamily: 'var(--font-mono, "SF Mono", Menlo, Consolas, monospace)',
      fontSize: 12,
      lineHeight: 1.5,
      backgroundColor: 'var(--color-neutral-100, #f3f4f6)',
      padding: '8px 12px',
      borderRadius: 4,
      overflow: 'auto',
      margin: '8px 0 12px'
    },
    '& pre code': {
      backgroundColor: 'transparent',
      padding: 0,
      borderRadius: 0
    },
    '& blockquote': {
      margin: '8px 0 12px',
      padding: '4px 12px',
      borderLeft: '3px solid var(--color-ai-300, #c4b5fd)',
      color: 'var(--color-neutral-700, #374151)'
    },
    '& table': {
      borderCollapse: 'collapse',
      margin: '8px 0 12px',
      fontSize: 13
    },
    '& th, & td': {
      border: '1px solid var(--color-neutral-200, #e5e7eb)',
      padding: '4px 8px',
      textAlign: 'left'
    },
    '& th': { backgroundColor: 'var(--color-neutral-50, #f9fafb)' },
    '& hr': {
      border: 'none',
      borderTop: '1px solid var(--color-neutral-200, #e5e7eb)',
      margin: '12px 0'
    }
  },
  severityInformational: {
    backgroundColor: 'var(--color-info-100, #e0f2fe)',
    color: 'var(--color-info-800, #075985)'
  },
  severityNotable: {
    backgroundColor: 'var(--color-warning-100, #fef3c7)',
    color: 'var(--color-warning-800, #92400e)'
  },
  severityConcerning: {
    backgroundColor: 'var(--color-danger-100, #fee2e2)',
    color: 'var(--color-danger-800, #991b1b)'
  },
  verdictValueCorrect: {
    backgroundColor: 'var(--color-success-100, #dcfce7)',
    color: 'var(--color-success-800, #166534)'
  },
  verdictDataIssue: {
    backgroundColor: 'var(--color-danger-100, #fee2e2)',
    color: 'var(--color-danger-800, #991b1b)'
  },
  verdictCalcMisunderstanding: {
    backgroundColor: 'var(--color-warning-100, #fef3c7)',
    color: 'var(--color-warning-800, #92400e)'
  },
  verdictInconclusive: {
    backgroundColor: 'var(--color-neutral-100, #f3f4f6)',
    color: 'var(--color-neutral-700, #374151)'
  },
  confidenceConfirmed: {
    backgroundColor: 'var(--color-ai-100, #e0e7ff)',
    color: 'var(--color-ai-800, #3730a3)'
  },
  confidenceLikely: {
    backgroundColor: 'var(--color-ai-050, #ede9fe)',
    color: 'var(--color-ai-700, #5b21b6)'
  },
  confidenceInconclusive: {
    backgroundColor: 'var(--color-neutral-100, #f3f4f6)',
    color: 'var(--color-neutral-700, #374151)'
  },
  category: {
    backgroundColor: 'var(--color-neutral-100, #f3f4f6)',
    color: 'var(--color-neutral-700, #374151)',
    fontFamily: 'var(--font-mono, "SF Mono", Menlo, Consolas, monospace)',
    fontSize: 11,
    fontWeight: 'var(--font-weight-medium, 500)'
  },

  emptyRoot: {
    padding: '20px 24px',
    borderRadius: 8,
    background: 'var(--color-neutral-50, #f9fafb)',
    border: '1px dashed var(--color-neutral-300, #d1d5db)',
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13,
    lineHeight: 1.5
  }
}))

const VERDICT_LABEL = {
  value_correct: 'Value correct',
  data_issue: 'Data issue',
  calc_misunderstanding: 'Calc misunderstanding',
  inconclusive: 'Inconclusive'
}
const CONFIDENCE_LABEL = {
  confirmed: 'Confirmed',
  likely: 'Likely',
  inconclusive: 'Inconclusive'
}
const SEVERITY_LABEL = {
  informational: 'Informational',
  notable: 'Notable',
  concerning: 'Concerning'
}

export default function AnswerCard ({ answer }) {
  const classes = useStyles()

  if (!answer || (!answer.summary && !answer.verdict && !answer.confidence)) {
    return (
      <div className={classes.emptyRoot}>
        The agent completed the investigation without recording a final answer.
      </div>
    )
  }

  const { verdict, confidence, severity, category, summary } = answer

  return (
    <div
      className={clsx(classes.root, {
        [classes.rootInformational]: severity === 'informational',
        [classes.rootNotable]: severity === 'notable',
        [classes.rootConcerning]: severity === 'concerning'
      })}
    >
      <div className={classes.badges}>
        {verdict && (
          <Chip
            size='small'
            label={VERDICT_LABEL[verdict] || verdict}
            className={clsx(classes.badge, {
              [classes.verdictValueCorrect]: verdict === 'value_correct',
              [classes.verdictDataIssue]: verdict === 'data_issue',
              [classes.verdictCalcMisunderstanding]: verdict === 'calc_misunderstanding',
              [classes.verdictInconclusive]: verdict === 'inconclusive'
            })}
          />
        )}
        {severity && (
          <Chip
            size='small'
            label={SEVERITY_LABEL[severity] || severity}
            className={clsx(classes.badge, {
              [classes.severityInformational]: severity === 'informational',
              [classes.severityNotable]: severity === 'notable',
              [classes.severityConcerning]: severity === 'concerning'
            })}
          />
        )}
        {confidence && (
          <Chip
            size='small'
            label={CONFIDENCE_LABEL[confidence] || confidence}
            className={clsx(classes.badge, {
              [classes.confidenceConfirmed]: confidence === 'confirmed',
              [classes.confidenceLikely]: confidence === 'likely',
              [classes.confidenceInconclusive]: confidence === 'inconclusive'
            })}
          />
        )}
        {category && (
          <Chip size='small' label={category} className={clsx(classes.badge, classes.category)} />
        )}
      </div>
      {summary && (
        <div className={classes.summary}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary}</ReactMarkdown>
        </div>
      )}
    </div>
  )
}

AnswerCard.propTypes = {
  answer: PropTypes.shape({
    summary: PropTypes.string,
    verdict: PropTypes.string,
    confidence: PropTypes.string,
    severity: PropTypes.string,
    category: PropTypes.string
  })
}
